import * as React from 'react'
import { useEffect, useState } from 'react'
import { withTranslations } from 'helpers'

import TouchWrapper from '../TouchWrapper'
import HeroItem from './HeroItem'
import HeroNav from './HeroNav'
import HeroProgress from './HeroProgress'

/**
 * React component for a hero image slider.
 * @function
 * @param {HeroProps} props - Props for the component.
 * @returns {JSX.Element | null} - A JSX element representing the component.
 */

const Hero = ({ items }) => {
  const [activeItem, setActiveItem] = useState(0)
  const [autoScrolling, setAutoScrolling] = useState(true)

  const updateActiveItem = (direction) => {
    const lastItem = items.length - 1

    if (['left', 'next'].indexOf(direction) !== -1) {
      setActiveItem(activeItem === lastItem ? 0 : activeItem + 1)
    } else {
      setActiveItem(activeItem === 0 ? lastItem : activeItem - 1)
    }
  }

  const handleNagivation = (direction) => {
    setAutoScrolling(false)
    updateActiveItem(direction)
  }

  useEffect(() => {
    if (autoScrolling) {
      const interval = setInterval(() => {
        updateActiveItem('next')
      }, 5000)

      return () => clearInterval(interval)
    }
  }, [autoScrolling, updateActiveItem])

  if (!items.length) {
    return null
  }

  const { invertTextColor } = items[activeItem]

  return (
    <div className="hero">
      <TouchWrapper className="hero__touch-wrapper" onSwipe={handleNagivation}>
        {items.map((item, index) => (
          <HeroItem
            key={`hero-item-${index}`}
            position={item.position}
            title={item.title}
            active={activeItem === index}
            subtitle={item.subtitle}
            content={item.content}
            mobileImgSrc={item.mobileImgSrc}
            imgSrc={item.imgSrc}
            invertTextColor={item.invertTextColor}
            link={item.link}
            buttonText={item.buttonText}
          />
        ))}
        {items.length > 1 && (
          <div className="hero__controls">
            <HeroNav
              activeItemIndex={activeItem}
              invertTextColor={invertTextColor}
              totalItemCount={items.length}
              onNavigate={handleNagivation}
            />
            <HeroProgress
              autoScrolling={autoScrolling}
              activeItemIndex={activeItem}
              invertTextColor={invertTextColor}
              totalItemCount={items.length}
            />
          </div>
        )}
      </TouchWrapper>
    </div>
  )
}

/**
 * @typedef {{
 * title: string
 * subtitle: string
 * content: string
 * position: string
 * link: string
 * imgSrc: string
 * mobileImgSrc: string
 * invertTextColor: boolean
 * buttonText: string
 * }} HeroItem
 */

/**
 * @typedef {{
 *  items: HeroItem[]
 * }} HeroProps
 */

export default withTranslations(Hero)
